import type { TemplateResult } from 'lit';
import { css, html, nothing } from 'lit';
import './d-page-header.js';
import '../library/editors/elements/d-select-dropdown.js';
import '../library/components/d-help-section.js';
import '../library/elements/d-action.js';
import 'src/library/components/d-field-section';
import 'src/library/lists/d-list-section';
import { property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import type { PageViewModel } from '@trinnvis/dabih-api-client';
import type { AbstractPageView } from './abstract-page-view.js';
import { levelContentStyles } from 'src/library/level-content-styles.js';
import { getPdfTemplateDefinitionForPage } from 'src/store/selectors/pdf-template-functions';
import type { FeatureStates } from '../store/selectors/featureStates';
import type { IconEnum } from './icon-enum';
import type { ListSectionItemInput } from '../library/lists/listSectionItemInput';
import type { TableDefinition } from '../library/abstracts/table-definition';
import type { ListDefinition } from '../content/list-definition';
import type { SectionField } from '../library/components/section-field';
import type { OverviewPageViewModel } from 'src/pages/overview-page/overview-page-view-model';
import { ResponsiveElement } from 'src/library/elements/responsive-container';

/**
 *
 *
 */
export class PageContent<T extends AbstractPageView> extends ResponsiveElement {
  static readonly styles = [
    levelContentStyles,
    css`
      d-promo-section {
        max-width: 100%;
        margin-top: 12px;
        padding-right: calc(100% - 724px);
      }
    `,
  ];
  @property({ type: Object })
  pageView!: T;
  @property({ type: String })
  icon: IconEnum = '';
  @property({ type: Boolean })
  singleUserVersion = false;
  @property({ type: Number })
  scrollLeft = 0;
  @property({ type: Array })
  pages: PageViewModel[] = [];
  @property({ type: String })
  editHref = '';
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: String })
  selectValue = '';
  @property({ type: Array })
  selectItems = [];
  @property({ type: Array })
  activityCodes = [];
  @property({ type: String })
  pdfLink = '';
  @property({ type: String })
  customPageAction = '';
  @property({ type: Boolean })
  uncoverPageMenu = false;
  @property({ type: Boolean })
  headerTop = false;
  @property({ type: Boolean })
  showClose = false;
  /**
   * The covered property. If the page is shown below other content views then the page is covered.
   */
  @property({ type: Boolean, reflect: true })
  covered = false;
  @property({ type: Boolean, reflect: true })
  top = false;
  @property({ type: Boolean, reflect: true, attribute: 'wide-page' })
  widePage = false;
  @property({ type: Number })
  contentStickyTop = 0;
  @property({ type: Boolean, attribute: 'prevent-header-shrink', reflect: true })
  preventHeaderShrink = false;
  @property({ type: Number })
  pageWidth = 1000;
  @property({ type: Object })
  featureStates!: FeatureStates;
  @property({ type: Boolean })
  storybook = false;

  protected get currentItemLabel() {
    return this.pageView.name;
  }

  protected get currentItemSublabel() {
    return '';
  }

  onShareContent() {
    this.dispatchEvent(
      new CustomEvent('share-content', { bubbles: true, composed: true, detail: { message: '', contacts: [] } }),
    );
  }

  setContentStickyTop(height: number) {
    this.contentStickyTop = height;
  }

  renderHeader() {
    return html`
      <d-page-header
        ?no-margin=${this.pageView['type'] === 'staffing-page'}
        style="top: 0px;"
        .icon=${this.pageView.icon}
        .label=${this.currentItemLabel}
        .sublabel=${this.currentItemSublabel}
        .href=${this.noTrailingSlash(this.pageView.href)}
        ?covered=${this.covered}
        @height-changed=${(e) => this.setContentStickyTop(e.detail)}
      >
        ${this.renderBeforeHeaderLabel()} ${this.renderCustomActions()} ${this.renderClose()}
      </d-page-header>
    `;
  }

  render() {
    return html`
      ${this.renderPdfData()} ${this.renderHeader()}
      <div class="content" id="content">${this.renderHelp()} ${this.renderContent()}</div>
    `;
  }

  noTrailingSlash(href: string) {
    return href.endsWith('/') ? href.substring(0, href.length - 1) : href;
  }

  renderPdfData() {
    const showPdfData = localStorage.getItem('showPdfData');
    if (showPdfData === 'TRUE') {
      return html`<textarea>
      ${getPdfTemplateDefinitionForPage(
          this.viewFields(),
          this.tables(this.pageView),
          this.lists(),
          this.pageView,
        )}</textarea
      >`;
    }
    return nothing;
  }

  renderFieldSection(entityType: string, fields: SectionField[], item: any) {
    return html`
      <d-field-section
        .entityType=${entityType}
        .fields=${fields}
        .item=${item}
        .contentStickyTop=${this.contentStickyTop}
        @field-action=${(e) => this.onFieldAction(e)}
      ></d-field-section>
    `;
  }

  protected renderBeforeHeaderLabel() {
    return html``;
  }

  protected renderHelp() {
    return html`
      <d-help-section
        .helpOnly=${this.isOverviewPage(this.pageView) && this.pageView.lists.length === 0}
        content="${this.pageView.helpContent}"
      ></d-help-section>
    `;
  }

  protected renderContent(): TemplateResult | typeof nothing {
    return nothing;
  }

  /**
   * Renders page actions. Default actions include only "Share".
   * @protected
   */
  protected renderCustomActions(): TemplateResult | typeof nothing {
    return this.singleUserVersion
      ? nothing
      : html`<d-action class="action" @click=${() => this.onShareContent()}>${t('content.share')}</d-action>`;
  }

  protected renderClose(): TemplateResult | typeof nothing {
    return this.showClose
      ? html`<d-action .href=${'/account/' + this.pageView.organizationId}>${t('common:close')}</d-action>`
      : nothing;
  }

  protected viewFields(): SectionField[] {
    return [];
  }

  protected tables(view: any = undefined): TableDefinition[] {
    if (view.type === 'nothing') {
      console.log(view);
    }
    return [];
  }

  protected onFieldAction(e) {
    this[e.detail.value]();
  }

  protected lists(): ListDefinition[] {
    return [];
  }

  protected renderListSections(): TemplateResult<1> {
    return html`
      ${this.lists()
        .filter((list) => !Object.hasOwn(list, 'condition') || list.condition)
        .map((list) => {
          if (list.type === 'custom' && list.render) {
            return list.render();
          }
          let items: ListSectionItemInput[] = [];
          if (list.items) {
            items = this.pageView[list.items];
          }
          return html`
            <d-list-section
              theme-page
              .actions=${list.actions ?? []}
              .field=${list.field ?? ''}
              .icon=${list.icon}
              .items=${items}
              .contentStickyTop=${this.contentStickyTop}
              @action=${(e: CustomEvent) => this.onListSectionAction(e.detail)}
            ></d-list-section>
          `;
        })}
    `;
  }

  private isOverviewPage(object: AbstractPageView): object is OverviewPageViewModel {
    return 'type' in object && object.type === 'overview-page';
  }

  private onListSectionAction(detail: string) {
    this[detail]();
  }
}
